import { ContainerTypesEnumType } from "@sellar-io/api-v2/types";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { SlideTransition } from "../../../../components/transition/SlideTransition";
import {
  DISCOVER_RECENTLY_ADDED,
  DISCOVER_SEARCH,
  DISCOVER_SUPPLIER_MAP,
  DISCOVER_SUPPLIERS_DIRECT,
  DISCOVER_SUPPLIERS_RECENT,
  DISCOVER_PRE_ORDER,
} from "../../../../constants/routes";
import {
  abvFilterType,
  AbvType,
  dietaryTypes,
  dietaryTypeValues,
  useDiscoveryV2SearchStore,
} from "../DiscoveryV2SearchStore";
import {
  draughtPriceFilterType,
  packagedPriceFilterType,
  PriceType,
} from "../filterCards/FilterPriceTypes";
import { filterStyles } from "~/components/marketSearch/marketSearchFilters";
import { SellarIconGradient } from "@sellar-io/components";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";

type MenuItemType = {
  content: ReactNode;
  onClick?: () => void;
  subItems?: MenuItemType[];
};

export const DiscoveryMenuBar = () => {
  const [selected, setSelected] = useState<number | undefined>();
  const navigate = useNavigate();
  const {
    addAbvType,
    addDietaryType,
    addPriceType,
    setContainerTypes,
    addStyleSearchValue,
    setCategoryKey,
    setIsSellarCreditElgibleOnly,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      addAbvType: state.addAbvType,
      addDietaryType: state.addDietaryType,
      addPriceType: state.addPriceType,
      setContainerTypes: state.setContainerTypes,
      addStyleSearchValue: state.addStyleSearchValue,
      setCategoryKey: state.setCategoryKey,
      setIsSellarCreditElgibleOnly: state.setIsSellarCreditEligibleOnly,
    })),
  );

  const generateStyleSearchValue = (
    childSearchValue: string,
    categorySearchValue: string,
  ) => {
    return [childSearchValue, categorySearchValue].join(" & ");
  };

  const { business } = useSellarCurrentBusiness();

  const sellarCreditEnabled = useRemoteFeatureFlags().isActive(
    "retailerSellarCreditCheckout",
  );

  const canShowSellarCreditOption =
    sellarCreditEnabled && business?.BusinessUser?.canAccessSellarCredit;

  const menuItems: MenuItemType[] = [
    ...(canShowSellarCreditOption
      ? [
          {
            content: (
              <FilterLabel
                customIcon={<SellarIconGradient />}
                text="Buy with Sellar Credit"
              />
            ),
            onClick: () => {
              setIsSellarCreditElgibleOnly(true);
              navigate(DISCOVER_SEARCH);
            },
          },
        ]
      : []),
    {
      content: <FilterLabel emoji="🍻" text="Beer" />,
      subItems: [
        {
          content: "All beer",
          onClick: () => {
            setCategoryKey(filterStyles["beer"].key);
            navigate(DISCOVER_SEARCH);
          },
        },
        ...(filterStyles["beer"] &&
          filterStyles["beer"].children.map((child) => ({
            content: child.displayValue,
            onClick: () => {
              addStyleSearchValue(child.searchValue);
              navigate(DISCOVER_SEARCH);
            },
          }))),
      ],
    },
    {
      content: <FilterLabel emoji="🍎" text="Cider" />,
      subItems: [
        {
          content: "All cider",
          onClick: () => {
            setCategoryKey(filterStyles["cider"].key);
            navigate(DISCOVER_SEARCH);
          },
        },
        ...(filterStyles["cider"] &&
          filterStyles["cider"].children.map((child) => ({
            content: child.displayValue,
            onClick: () => {
              addStyleSearchValue(
                generateStyleSearchValue(
                  child.searchValue,
                  filterStyles["cider"].searchValue,
                ),
              );
              navigate(DISCOVER_SEARCH);
            },
          }))),
      ],
    },
    {
      content: <FilterLabel emoji="🍸" text="Spirits" />,
      subItems: [
        ...(filterStyles["spirits"] &&
          filterStyles["spirits"].children.map((child) => ({
            content: child.displayValue,
            onClick: () => {
              addStyleSearchValue(child.searchValue);
              navigate(DISCOVER_SEARCH);
            },
          }))),
      ],
    },
    {
      content: <FilterLabel emoji="🥤" text="Softs" />,
      subItems: [
        ...(filterStyles["softDrinks"] &&
          filterStyles["softDrinks"].children.map((child) => ({
            content: child.displayValue,
            onClick: () => {
              addStyleSearchValue(child.searchValue);
              navigate(DISCOVER_SEARCH);
            },
          }))),
      ],
    },
    {
      content: <FilterLabel emoji="🍷" text="Wine" />,
      subItems: [
        {
          content: "All wine",
          onClick: () => {
            setCategoryKey(filterStyles["wine"].key);
            navigate(DISCOVER_SEARCH);
          },
        },
        ...(filterStyles["wine"] &&
          filterStyles["wine"].children.map((child) => ({
            content: child.displayValue,
            onClick: () => {
              addStyleSearchValue(
                generateStyleSearchValue(
                  child.searchValue,
                  filterStyles["wine"].searchValue,
                ),
              );
              navigate(DISCOVER_SEARCH);
            },
          }))),
      ],
    },
    {
      content: <FilterLabel emoji="🍯" text="Mead" />,
      subItems: [],
      onClick: () => {
        setCategoryKey(filterStyles["mead"].key);
        navigate(DISCOVER_SEARCH);
      },
    },
    {
      content: <FilterLabel emoji="📦" text="Containers" />,
      subItems: ["Keg", "Cask", "Can", "Bottle", "Bag In Box"].map(
        (container) => ({
          content: container,
          onClick: () => {
            let formattedContainer = container
              .toLowerCase()
              .replaceAll(" ", "");
            if (formattedContainer === "baginbox")
              formattedContainer = "bagInBox";
            setContainerTypes([formattedContainer] as ContainerTypesEnumType[]);
            navigate(DISCOVER_SEARCH);
          },
        }),
      ),
    },
    {
      content: <FilterLabel emoji="💰" text="Price" />,
      subItems: [
        ...Object.keys(packagedPriceFilterType)
          .filter(
            (priceTypeKey) =>
              !["between80And100", "over100"].includes(priceTypeKey),
          )
          .map((priceTypeKey) => ({
            content: packagedPriceFilterType[priceTypeKey as PriceType].name,
            onClick: () => {
              addPriceType(priceTypeKey as PriceType);
              navigate(DISCOVER_SEARCH);
            },
          })),
        ...Object.keys(draughtPriceFilterType)
          .filter(
            (priceTypeKey) =>
              !["lessThan40", "between40And60", "between60And80"].includes(
                priceTypeKey,
              ),
          )
          .map((priceTypeKey) => ({
            content: draughtPriceFilterType[priceTypeKey as PriceType].name,
            onClick: () => {
              addPriceType(priceTypeKey as PriceType);
              navigate(DISCOVER_SEARCH);
            },
          })),
      ],
    },
    {
      content: <FilterLabel emoji="🕒" text="Pre-orders" />,
      onClick: () => navigate(DISCOVER_PRE_ORDER),
    },
    {
      content: <FilterLabel emoji="🌾" text="Dietary" />,
      subItems: dietaryTypeValues.map((dietaryType) => ({
        content: dietaryTypes[dietaryType],
        onClick: () => {
          addDietaryType(dietaryType);
          navigate(DISCOVER_SEARCH);
        },
      })),
    },
    {
      content: <FilterLabel emoji="%" text="ABV" />,
      subItems: Object.keys(abvFilterType).map((abvTypeKey) => ({
        content: abvFilterType[abvTypeKey as AbvType].name,
        onClick: () => {
          addAbvType(abvTypeKey as AbvType);
          navigate(DISCOVER_SEARCH);
        },
      })),
    },
    {
      content: <FilterLabel emoji="🚚" text="Delivering direct" />,
      onClick: () => navigate(DISCOVER_SUPPLIERS_DIRECT),
    },
    {
      content: <FilterLabel emoji="⚡️" text="Latest releases" />,
      onClick: () => navigate(DISCOVER_RECENTLY_ADDED),
    },
    {
      content: <FilterLabel emoji="🆕" text="New suppliers" />,
      onClick: () => navigate(DISCOVER_SUPPLIERS_RECENT),
    },
    {
      content: <FilterLabel emoji="📍" text="Local to you" />,
      onClick: () => navigate(DISCOVER_SUPPLIER_MAP),
    },
  ];

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {menuItems.map((menuItem, idx) => (
          <MenuItem
            key={idx}
            onClick={() => {
              setSelected(idx === selected ? undefined : idx);
              menuItem.onClick?.();
            }}
            selected={idx === selected}
          >
            {menuItem.content}
          </MenuItem>
        ))}
      </div>
      <SlideTransition
        show={undefined !== selected}
        animate={true}
        className={twMerge("flex flex-col gap-3")}
      >
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {undefined !== selected &&
            menuItems[selected]?.subItems &&
            menuItems[selected].subItems.map((subItem, idx) => (
              <SubItem
                key={idx}
                onClick={() => subItem.onClick && subItem.onClick()}
              >
                {subItem.content}
              </SubItem>
            ))}
        </div>
      </SlideTransition>
    </div>
  );
};

const FilterLabel = ({
  customIcon,
  emoji,
  text,
}: {
  customIcon?: ReactNode;
  emoji?: string;
  text: string;
}) => {
  return (
    <>
      {!!customIcon && customIcon}
      {!!emoji && <span className="text-[24px]">{emoji}</span>}
      {text}
    </>
  );
};

export const MenuItem = ({
  children,
  onClick,
  selected,
  size = "medium",
}: {
  children: ReactNode;
  onClick: () => void;
  selected?: boolean;
  size?: "small" | "medium";
}) => {
  return (
    <button
      type="button"
      className={twMerge(
        "flex flex-none cursor-pointer items-center gap-1 rounded-md border border-zinc-300 bg-white text-sm hover:bg-zinc-100",
        selected && "border-zinc-500 bg-zinc-200 hover:bg-zinc-300",
        size === "small"
          ? "px-3 py-2 leading-[18px] font-medium"
          : "px-4 py-3 leading-[22px] font-semibold",
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const SubItem = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      className="flex-none cursor-pointer rounded-md border border-zinc-300 bg-zinc-100 px-3 py-2 text-sm leading-[18px] font-medium hover:bg-zinc-200"
      onClick={onClick}
    >
      {children}
    </button>
  );
};
